'use client';

import { useState } from 'react';

import { useGoBack } from '@/hooks/use-go-back';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { ChevronDown } from '@/icons';
import {
  Button as MokoButton,
  Separator,
  Skeleton,
  toast,
} from '@mokoboko/components';
import { PageContainer } from './page-container';
import {
  DetailCard,
  DETAIL_HERO,
  DETAIL_HERO_MEDIA,
  DETAIL_HERO_TEXT,
} from './detail-card';
import { ClassCalendar } from './class-calendar';
import { ClassCalendarSkeleton } from './class-calendar-skeleton';
import { AppearanceSettings } from '@/state/appearance-settings';
import {
  convertMinutesToTime,
  formatPrice,
  mergeClassNames,
} from '@/lib/utils';
import { Class } from '@/state/class-groups';
import { FaqSection } from './faq-section';
import useFaqs from '@/state/faqs';
import { ImageGallery, GALLERY_SKELETON } from './image-gallery';
import { getSortedImages } from '@/lib/entity-image';
import { DESCRIPTION_PROSE } from '@/lib/prose';
import { toPlainText } from '@/lib/plain-text';
import { getClassesByIds, getFixedDurationMinutes } from '@/helpers/class';
import { getClassRules } from '@/helpers/class-rules';
import { pricePillOf } from '@/helpers/credits';
import { openSitePage } from '@/helpers/embed-navigation';
import { findListedInstructor, ScheduleInstructor } from '@/helpers/instructor';
import { useIsEmbed } from '@/contexts/embed';
import { useAuth } from '@/contexts/auth';
import useAdminUsers, { AdminUser } from '@/state/admin-users';
import useClassGroups from '@/state/class-groups';
import useEffectivePrice from '@/state/effective-price';
import { INSTRUCTOR_PARAM, InstructorBioPanel } from './instructor-bio-panel';
import { InstructorPill } from './instructor-pill';
import { InstructorSection } from './instructor-section';
import { FactPill, LoneFactPill } from './fact-pill';
import { FactTiles } from './fact-tiles';
import {
  buildMinSpotsNotice,
  getMinSpotsPerBooking,
} from '@/helpers/min-spots';

// The public instructor list carries the bio but leaves out a paused
// instructor and is empty until it loads; the schedule's name and photo stand
// in, so the panel opens either way.
const toPanelInstructor = (
  scheduleInstructor: ScheduleInstructor,
  listed: AdminUser | undefined
): AdminUser =>
  listed ?? {
    ...scheduleInstructor,
    last_name: scheduleInstructor.last_name ?? undefined,
    role: 'INSTRUCTOR',
  };

const openInstructorOnSite = (slug: string, adminUserId: string) => {
  if (!openSitePage(`/classes/${slug}?${INSTRUCTOR_PARAM}=${adminUserId}`)) {
    toast.info('Allow pop-ups to open this instructor.');
  }
};

const ClassInstructorPanel = ({
  scheduleInstructor,
  listed,
  classId,
  open,
  onClose,
}: {
  scheduleInstructor: ScheduleInstructor;
  listed: AdminUser | undefined;
  classId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { data: classGroups } = useClassGroups({ include_classes: true });
  const instructor = toPanelInstructor(scheduleInstructor, listed);

  return (
    <InstructorBioPanel
      instructor={open ? instructor : null}
      classes={getClassesByIds(classGroups, instructor.class_ids).filter(
        (cls) => cls.class_id !== classId
      )}
      onClose={onClose}
    />
  );
};

export const ClassDetails = ({
  classData,
  classSettings,
  isRescheduling = false,
  orderId,
  slotId,
  instructorScheduleId,
  currentStartTime,
  rescheduleActiveQuantity = 1,
  rescheduleGuests = 0,
  openInstructorId,
}: {
  classData: Class;
  classSettings: AppearanceSettings['class'];
  isRescheduling: boolean;
  orderId?: string | null;
  slotId?: string | null;
  instructorScheduleId?: string | null;
  currentStartTime?: string | null;
  rescheduleActiveQuantity?: number;
  rescheduleGuests?: number;
  openInstructorId?: string | null;
}) => {
  const { goBack, hasBack } = useGoBack();
  const isEmbed = useIsEmbed();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { data: faqs } = useFaqs({
    entityType: 'CLASS',
    slug: classData.slug,
  });

  const scheduleInstructor =
    classSettings.show_instructor_name &&
    classData.instructor_schedules?.length === 1
      ? classData.instructor_schedules[0].admin_user
      : undefined;
  const { data: listedInstructors } = useAdminUsers({
    role: 'INSTRUCTOR',
    enabled: !!scheduleInstructor,
  });
  const listed = scheduleInstructor
    ? findListedInstructor(listedInstructors, scheduleInstructor.admin_user_id)
    : undefined;
  const instructorBio = toPlainText(listed?.bio);
  const [isBioOpen, setIsBioOpen] = useState(
    () =>
      !!openInstructorId &&
      openInstructorId === scheduleInstructor?.admin_user_id
  );
  const openBio = (adminUserId: string) =>
    isEmbed
      ? openInstructorOnSite(classData.slug, adminUserId)
      : setIsBioOpen(true);

  const fixedDurationMinutes = classSettings?.show_duration
    ? getFixedDurationMinutes(classData)
    : null;
  const showPrice = classSettings.show_price;
  const { data: effectivePriceData, isLoading: isPriceLoading } =
    useEffectivePrice({
      class_id: classData.class_id,
      enabled: showPrice && !isRescheduling,
    });
  // A signed-in visitor may hold a credit for this class, so the cash price
  // waits for the answer rather than flashing first.
  const pricePill =
    showPrice && !(user && isPriceLoading)
      ? pricePillOf(
          effectivePriceData?.effective_price,
          formatPrice(classData?.price, classData?.currency)
        )
      : null;
  const minSpots = getMinSpotsPerBooking(classData);
  const rules = getClassRules(classData);
  const loneRule = rules.length === 1 ? rules[0] : null;
  const hasPhonePill =
    !!scheduleInstructor || fixedDurationMinutes != null || !!pricePill;

  const sortedImages = classData.images
    ? getSortedImages(classData.images)
    : [];
  const showImage = classSettings.show_image;
  const hasImages = sortedImages.length > 0 || !!classData.image;

  return (
    <PageContainer>
      {scheduleInstructor && !isEmbed && (
        <ClassInstructorPanel
          scheduleInstructor={scheduleInstructor}
          listed={listed}
          classId={classData.class_id}
          open={isBioOpen}
          onClose={() => setIsBioOpen(false)}
        />
      )}
      <DetailCard
        hasBack={hasBack}
        // The site shell leaves room below the card for the phone book bar;
        // an embed does not, so its last section would sit under the bar.
        className={isEmbed ? 'pb-28 sm:pb-28' : undefined}
      >
        {hasBack && (
          <>
            <MokoButton
              variant="secondary-gray"
              className="w-fit"
              leadingIcon={<ChevronDown rotate={90} />}
              onClick={() => goBack('/classes')}
              size={isMobile ? 'lg' : 'md'}
            >
              Go back
            </MokoButton>
            <Separator />
          </>
        )}

        {!isRescheduling && (
          <>
            <div className={DETAIL_HERO}>
              <div
                className={mergeClassNames(
                  DETAIL_HERO_TEXT,
                  hasImages && showImage ? 'order-2 md:order-1' : ''
                )}
              >
                <div className="flex flex-col gap-3 sm:gap-2">
                  <h1 className="heading-page">{classData?.name}</h1>

                  <div
                    className={mergeClassNames(
                      'flex flex-wrap items-center gap-2 empty:hidden',
                      !hasPhonePill && 'max-sm:hidden'
                    )}
                  >
                    {scheduleInstructor && (
                      <InstructorPill
                        compact
                        instructor={scheduleInstructor}
                        onClick={() =>
                          openBio(scheduleInstructor.admin_user_id)
                        }
                      />
                    )}
                    {fixedDurationMinutes != null && (
                      <FactPill>
                        {convertMinutesToTime(fixedDurationMinutes)}
                      </FactPill>
                    )}
                    {pricePill && <FactPill>{pricePill}</FactPill>}
                    {loneRule && <LoneFactPill tile={loneRule} />}
                  </div>
                </div>

                {minSpots > 1 && (
                  <div className="rounded-xl bg-gray-100 px-5 py-3">
                    <p className="text-center text-sm text-gray-900">
                      {buildMinSpotsNotice(minSpots)}
                    </p>
                  </div>
                )}

                {classSettings.show_description &&
                  (classData?.description || classData?.short_description) && (
                    <div
                      className={DESCRIPTION_PROSE}
                      dangerouslySetInnerHTML={{
                        __html:
                          classData?.description ??
                          classData?.short_description ??
                          '',
                      }}
                    />
                  )}
              </div>

              {hasImages && showImage && (
                <div className={DETAIL_HERO_MEDIA}>
                  <ImageGallery
                    images={sortedImages}
                    alt={classData.name}
                    fallbackUrl={classData.image}
                  />
                </div>
              )}
            </div>
            <Separator />
          </>
        )}
        <ClassCalendar
          classData={classData}
          isRescheduling={isRescheduling}
          orderId={orderId}
          slotId={slotId}
          classSettings={classSettings}
          currentStartTime={currentStartTime}
          rescheduleActiveQuantity={rescheduleActiveQuantity}
          rescheduleGuests={rescheduleGuests}
        />
        {!isRescheduling && <FactTiles tiles={rules} />}
        {/* An instructor with no bio would only repeat the pill under the
            title. */}
        {!isRescheduling && scheduleInstructor && instructorBio !== '' && (
          <>
            <Separator />
            <InstructorSection
              instructor={scheduleInstructor}
              bio={instructorBio}
              onOpen={() => openBio(scheduleInstructor.admin_user_id)}
            />
          </>
        )}
        {faqs.length > 0 && (
          <>
            <Separator />
            <FaqSection faqs={faqs} compact />
          </>
        )}
      </DetailCard>
    </PageContainer>
  );
};

export const ClassDetailsSkeleton = () => {
  const { hasBack } = useGoBack();

  return (
    <PageContainer>
      <DetailCard hasBack={hasBack}>
        {hasBack && (
          <>
            <Skeleton
              isRounded
              className="!h-11 w-[124px] sm:!h-10 sm:w-[108px]"
            />
            <Separator />
          </>
        )}

        <div className={DETAIL_HERO}>
          <div
            className={mergeClassNames(DETAIL_HERO_TEXT, 'order-2 md:order-1')}
          >
            <div className="flex flex-col gap-3 sm:gap-2">
              <Skeleton text="h2" width={224} />
              <div className="flex flex-wrap items-center gap-2">
                <Skeleton isRounded className="!h-9 w-28 !rounded-full" />
                <Skeleton isRounded className="!h-9 w-20 !rounded-full" />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" />
              <Skeleton text="body-sm" className="w-2/3" />
            </div>
          </div>

          <div className={DETAIL_HERO_MEDIA}>
            <Skeleton isRounded className={GALLERY_SKELETON} />
          </div>
        </div>

        <Separator />

        <ClassCalendarSkeleton />
      </DetailCard>
    </PageContainer>
  );
};
